import { combineReducers, configureStore } from "@reduxjs/toolkit";
import { setupListeners } from "@reduxjs/toolkit/query";
import {
  createMigrate,
  FLUSH,
  PAUSE,
  PERSIST,
  persistReducer,
  persistStore,
  PURGE,
  REGISTER,
  REHYDRATE,
} from "redux-persist";
import autoMergeLevel2 from "redux-persist/lib/stateReconciler/autoMergeLevel2";
import storage from "redux-persist/lib/storage";

import appIdsSlice from "../../features/app/appIdsSlice";
import appReducer from "../../features/app/appSlice";
import featureFlagsSlice from "../../features/app/featureFlagsSlice";
import loginSlice from "../../features/Login/loginSlice";
import logoutSlice from "../../features/Logout/logoutSlice";
import registerDeviceSlice from "../../features/RegisterDevice/registerDeviceSlice";
import { deviceManagementApi } from "../services/deviceManagementApi";
import { fastrAppLinksApi } from "../services/fastrAppLinksApi";
import { featureFlagsApi } from "../services/featureFlagsApi";
import { formsInfoApi } from "../services/formsApi";
import { oauthApi } from "../services/oauthApi";
import { patientAuthenticationApi } from "../services/patientAuthenticationApi";
import { patientSettingsApi } from "../services/patientSettingsApi";
import { pdfFormsApi } from "../services/pdfFormsApi";
import { viewerApi } from "../services/viewerApi";
import { welcomePageApi } from "../services/welcomePageApi";
import formsDataSlice from "../shared/FormList/formsDataSlice";
import { migration } from "./migration";
import CURRENT_STORE_VERSION from "./version";

type TReducers = ReturnType<typeof rootReducer>;

const persistConfig = {
  key: "document-manager-root",
  storage,
  whitelist: ["appIdsSlice", "featureFlagsState"],
  stateReconciler: autoMergeLevel2,
  version: CURRENT_STORE_VERSION,
  migrate: createMigrate(migration),
};

export const rootReducer = combineReducers({
  [welcomePageApi.reducerPath]: welcomePageApi.reducer,
  [formsInfoApi.reducerPath]: formsInfoApi.reducer,
  [viewerApi.reducerPath]: viewerApi.reducer,
  [patientAuthenticationApi.reducerPath]: patientAuthenticationApi.reducer,
  [oauthApi.reducerPath]: oauthApi.reducer,
  [deviceManagementApi.reducerPath]: deviceManagementApi.reducer,
  [fastrAppLinksApi.reducerPath]: fastrAppLinksApi.reducer,
  [patientSettingsApi.reducerPath]: patientSettingsApi.reducer,
  [featureFlagsApi.reducerPath]: featureFlagsApi.reducer,
  [pdfFormsApi.reducerPath]: pdfFormsApi.reducer,
  appState: appReducer,
  appIdsSlice: appIdsSlice,
  logoutState: logoutSlice,
  formsDataState: formsDataSlice,
  registerDeviceState: registerDeviceSlice,
  featureFlagsState: featureFlagsSlice,
  loginSlice: loginSlice,
});

const persistedReducer = persistReducer<TReducers>(persistConfig, rootReducer);

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        // pdfFormsApi intentionally holds non-serializable values: getPdfForm
        // caches a Blob (its fulfilled action payload + its cache slice) and
        // updatePdfForm dispatches a File. Exempt the getPdfForm fulfilled action
        // (Blob payload) and pdfFormsApi's cache slice so it doesn't spam
        // non-serializable-value warnings in the dev console. The File arg is
        // already covered by RTK's default `meta.arg` ignore; we deliberately do
        // NOT override `ignoredActionPaths` so its other default
        // (`meta.baseQueryMeta`, which holds the fetch Request/Response on every
        // RTK Query action) stays exempt.
        ignoredActions: [
          FLUSH,
          REHYDRATE,
          PAUSE,
          PERSIST,
          PURGE,
          REGISTER,
          `${pdfFormsApi.reducerPath}/executeQuery/fulfilled`,
        ],
        ignoredPaths: [pdfFormsApi.reducerPath],
      },
    }).concat(
      welcomePageApi.middleware,
      viewerApi.middleware,
      formsInfoApi.middleware,
      patientAuthenticationApi.middleware,
      oauthApi.middleware,
      deviceManagementApi.middleware,
      fastrAppLinksApi.middleware,
      patientSettingsApi.middleware,
      featureFlagsApi.middleware,
      pdfFormsApi.middleware
    ),
});

setupListeners(store.dispatch);

export const persistor = persistStore(store);
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
